import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { ROUTES, APP_MENU } from '../../../../constants';
import { CRM_ONBOARDING_ACTIVE_EVENT } from '../../../../containers/GovCrm/Contacts/helpers';
import { useMediaQueryDown } from '../../../../hooks/useMediaQuery';
import { useApplication } from '../../../../services/application';
import { useTrans } from '../../../../services/i18n';
import translation from '../../../../services/i18n/translations/en.json';
import { emptyArray } from '../../../../utils/arrayUtils';
import { useFormsAccess } from '../../../../hooks/useFormsAccess';
import { BrandLogo } from '../../../common/BrandLogo';
import { Paper, SpacingContainer } from '../../../common/Containers';
import MediumBanner from '../../../common/Subscription/BecomePremiumTopBar/MidBanner';
import connect from '../AppHeader/connect';
import EZSearch from '../EZSearch';
import { useAppMenu } from './hooks/useActiveGroup';
import { getVisibleSubmenuItems } from './getVisibleSubmenuItems';
import { renderAppMenuGroup } from './renderAppMenuGroup';
import useStyles from './styles';
import Badge from '@material-ui/core/Badge';
import { useDispatch, useSelector } from 'react-redux';
import { actions as todoActions } from '../../../../store/todoList';
import { makeStyles } from '@material-ui/core/styles';

// Pulse animation for todo badge
const useBadgeStyles = makeStyles({
  badgePulse: {
    animation: '$badgePulseAnim 8s ease-in-out infinite',
    transformOrigin: 'center',
  },
  '@keyframes badgePulseAnim': {
    '0%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(46, 107, 255, 0)',
    },
    '20%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(46, 107, 255, 0)',
    },
    '40%': {
      transform: 'scale(1.18)',
      boxShadow: '0 0 0 6px rgba(46, 107, 255, 0.35)',
    },
    '60%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(46, 107, 255, 0)',
    },
    '100%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(46, 107, 255, 0)',
    },
  },
});

const AppMenu = ({
  openDrawer,
  setDrawerClose,
  settings,
  subscription,
  currentOnboardingStep,
  authUser,
  getAvatarDownloadUrl,
  counts,
}) => {
  const badgeClasses = useBadgeStyles();
  const classes = useStyles();
  const { trans } = useTrans();
  const { canInteractWithForms } = useFormsAccess();
  const isSmallScreen = useMediaQueryDown('sm');
  const [pendingLMS, setPendingLMS] = useState(false);
  const [crmTourActive, setCrmTourActive] = useState(false);
  const lastAutoOpenedRouteRef = useRef('');

  const { menu, setActive } = useAppMenu(subscription?.data);
  const location = useLocation();

  useEffect(() => {
    const handleCrmTourActive = (event) => {
      setCrmTourActive(Boolean(event.detail?.active));
    };

    window.addEventListener(CRM_ONBOARDING_ACTIVE_EVENT, handleCrmTourActive);
    return () => window.removeEventListener(CRM_ONBOARDING_ACTIVE_EVENT, handleCrmTourActive);
  }, []);
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const activeMenuKey = searchParams.get('menu');

  const isOnDashboard = location.pathname === ROUTES.ROUTE_DASHBOARD.path;
  const isOnRegistrationForms = location.pathname === ROUTES.ROUTE_FORMS.path && activeMenuKey !== 'marketing-sap-form';

  const showMenuText = openDrawer;

  const dispatch = useDispatch();
  const { todoCounts, sap, sapFormId } = useSelector((state) => ({
    todoCounts: state.todoList?.counts || { completed: 0, notCompleted: 0, notCompletedForms: 0 },
    sap: state.todoList?.sap || null,
    sapFormId: state.todoList?.sapFormId || null,
  }));

  const application = useApplication();
  const [logoURL, setLogoURL] = useState();

  function getWindowSize() {
    const { innerWidth, innerHeight } = window;
    return { innerWidth, innerHeight };
  }

  const [windowSize, setWindowSize] = useState(getWindowSize());

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize());
    }

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (settings.nameImageBranding != null) {
      if (!settings.externalImageBranding) {
        getAvatarDownloadUrl(settings.nameImageBranding).then((value) => setLogoURL(value.url));
      } else {
        setLogoURL(settings.nameImageBrandingUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => {
    dispatch(todoActions.getToDoList({}));
  }, [dispatch]);

  useEffect(() => {
    if (!menu || !menu.length) return;

    const routeKey = `${location.pathname}${location.search}`;

    if (lastAutoOpenedRouteRef.current === routeKey) {
      return;
    }

    if (activeMenuKey === 'marketing-sap-form') {
      const marketingIndex = menu.findIndex((group) => group.key === 'Marketing');

      if (marketingIndex >= 0) {
        setActive(marketingIndex);
        lastAutoOpenedRouteRef.current = routeKey;
      }

      return;
    }

    if (location.pathname === ROUTES.ROUTE_FORMS.path) {
      const registrationIndex = menu.findIndex((group) => group.key === 'Registration');

      if (registrationIndex >= 0) {
        setActive(registrationIndex);
        lastAutoOpenedRouteRef.current = routeKey;
      }

      return;
    }

    lastAutoOpenedRouteRef.current = routeKey;
  }, [activeMenuKey, location.pathname, location.search, menu, setActive]);

  const activeGroupOnboardingTour = (key) => {
    const onboardingStep = APP_MENU.findIndex((group) => group.key === key) + 1;

    return onboardingStep > 0 && currentOnboardingStep === onboardingStep;
  };

  const executeStringFunction = (fnstring) => {
    switch (fnstring) {
      case 'goToLearnWorlds':
        goToLearnWorlds();
        break;
      default:
        break;
    }
  };

  const clickOnLink = async (event, route, groupIndex, groupKey) => {
    setActive(groupIndex);

    const isMarketingSapFormRoute = groupKey === 'Marketing' && route.key === 'marketing-sap-form';

    if (isMarketingSapFormRoute) {
      event.preventDefault();

      try {
        const payload = await dispatch(todoActions.getToDoList({})).unwrap();

        const freshSap = payload?.sap ?? null;
        const freshSapFormId = payload?.sapFormId ?? null;

        const nextPath =
          canInteractWithForms && freshSap !== 'completed' && freshSapFormId
            ? `/client-portal/forms/${freshSapFormId}?menu=marketing-sap-form`
            : `${ROUTES.ROUTE_FORMS.path}?menu=marketing-sap-form`;

        history.push(nextPath);
      } catch (error) {
        const fallbackPath = getRoutePath(groupKey, route);
        history.push(fallbackPath);
      }

      if (isSmallScreen) {
        setDrawerClose();
      }

      return;
    }

    if (route.function) {
      executeStringFunction(route.function);
    }

    if (isSmallScreen) {
      setDrawerClose();
    }
  };

  const getRoutePath = (groupKey, route) => {
    const isMarketingSapFormRoute = groupKey === 'Marketing' && route.key === 'marketing-sap-form';

    if (isMarketingSapFormRoute) {
      if (canInteractWithForms && sap !== 'completed' && sapFormId) {
        return `/client-portal/forms/${sapFormId}?menu=marketing-sap-form`;
      }

      return `${ROUTES.ROUTE_FORMS.path}?menu=marketing-sap-form`;
    }

    return route.path === 'none' ? '#' : route.path;
  };

  const getRouteTitle = (route) => {
    return route.menuTitle || safeTrans(route.key);
  };

  const safeTrans = (key) => {
    if (Object.prototype.hasOwnProperty.call(translation, key)) {
      return trans(key);
    }

    return key;
  };

  const getNavIsActive = (route) => (match, currentLocation) => {
    const pathname = currentLocation.pathname;
    const currentSearchParams = new URLSearchParams(currentLocation.search);
    const currentActiveMenuKey = currentSearchParams.get('menu');

    if (route.key === 'Forms') {
      return pathname === ROUTES.ROUTE_FORMS.path && currentActiveMenuKey !== 'marketing-sap-form';
    }

    if (route.key === 'marketing-sap-form') {
      return currentActiveMenuKey === 'marketing-sap-form' || pathname.startsWith('/client-portal/forms/');
    }

    if (match) return true;

    return (route.nested || []).some((nestedRoute) =>
      Boolean(
        matchPath(pathname, {
          path: nestedRoute.path,
        })
      )
    );
  };

  const goToLearnWorlds = async () => {
    if (pendingLMS) return;
    setPendingLMS(true);
    const res = await application.call('learnWorlds.singleSignOn', {});
    setPendingLMS(false);
    window.open(res.lw.url, '_blank');
  };

  const renderRouteContent = (route, isWorkplaceOpen) => {
    const badgeContent = (() => {
      switch (route.key) {
        case 'pipelines-pursuits-tasks':
          return counts.pipelineTasksCount;
        case 'personal-tasks':
          return counts.personalTasksCount;
        case 'dashboard':
          return openDrawer && todoCounts.notCompleted > 0 && isWorkplaceOpen ? todoCounts.notCompleted : null;
        case 'Forms':
          return openDrawer && todoCounts.notCompletedForms > 0 ? todoCounts.notCompletedForms : null;
        case 'marketing-sap-form':
          return null;
        default:
          return null;
      }
    })();

    if (badgeContent !== null) {
      const isDashboardRoute = route.key === 'dashboard';
      const isRegistrationFormsRoute = route.key === 'Forms';

      return (
        <span style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', columnGap: 8 }}>
          <span>{getRouteTitle(route)}</span>
          <Badge
            style={{ top: '0.4rem' }}
            badgeContent={badgeContent}
            color="secondary"
            overlap="rectangular"
            classes={
              (isDashboardRoute && !isOnDashboard) || (isRegistrationFormsRoute && !isOnRegistrationForms)
                ? { badge: badgeClasses.badgePulse }
                : undefined
            }
          />
        </span>
      );
    }

    return getRouteTitle(route);
  };

  const brandLogo = openDrawer ? (
    <BrandLogo height={64} style={{ marginLeft: '10px' }} />
  ) : (
    <BrandLogo variant="shield" height={40} style={{ marginLeft: '15px' }} />
  );

  return (
    <Drawer
      variant="permanent"
      anchor={isSmallScreen ? 'bottom' : 'left'}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: openDrawer,
        [classes.drawerClose]: !openDrawer,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: openDrawer,
          [classes.drawerClose]: !openDrawer,
          [classes.drawerLitleHeight]: !openDrawer && windowSize.innerHeight < 705,
        }),
      }}
      style={{
        zIndex: currentOnboardingStep > 0 ? 1702 : undefined,
      }}
    >
      <Paper
        component={SpacingContainer}
        style={{
          borderRadius: 0,
          height: '100%',
          paddingTop: '10px',
        }}
      >
        {((authUser && !authUser.onViewTour && currentOnboardingStep > 0) || crmTourActive) && (
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.55)',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              overflow: 'hidden',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          ></div>
        )}

        {isSmallScreen && (
          <div className={classes.containerEzSearch}>
            <EZSearch setDrawerClose={setDrawerClose} />
          </div>
        )}

        <div className={classes.topPart}>
          <a href={ROUTES.ROUTE_DASHBOARD.path}>
            {logoURL ? (
              <img src={logoURL} style={{ width: 'auto', height: '64px', marginLeft: '10px' }} alt="" />
            ) : (
              brandLogo
            )}
          </a>
        </div>

        <Grid container direction={'column'} spacing={3} className={classes.menuList}>
          {settings &&
            !emptyArray(settings.permissions) &&
            menu.map((group, groupIndex) => {
              const submenuItems = getVisibleSubmenuItems(group, settings);
              if (!submenuItems) return null;

              return renderAppMenuGroup({
                group,
                groupIndex,
                submenuItems,
                authUser,
                currentOnboardingStep,
                openDrawer,
                showMenuText,
                isOnDashboard,
                todoCounts,
                pendingLMS,
                classes,
                badgeClasses,
                safeTrans,
                activeGroupOnboardingTour,
                setActive,
                clickOnLink,
                getRoutePath,
                getNavIsActive,
                renderRouteContent,
              });
            })}
        </Grid>

        <br />
        <div className={classes.containerPremiumBanner}>
          <MediumBanner collapsed={!openDrawer} />
        </div>
      </Paper>
    </Drawer>
  );
};

export default connect(AppMenu);
